'use client'

import { useState } from 'react'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/nextjs'
import { ThemeProvider } from 'next-themes'
import { TooltipProvider } from '@/components/ui/tooltip'
import { reportMutationError } from '@/lib/api/report-mutation-error'

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // 30s default — queries younger than this won't refetch on remount / focus.
            // Override per-query: `staleTime: Infinity` for settings-like data that only
            // changes on a mutation we control; `staleTime: 0` only in rare always-fresh cases.
            // See the `data-loading-and-mutations` skill.
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: 2,
          },
        },
        queryCache: new QueryCache({
          onError: (error) => {
            Sentry.captureException(error, { tags: { area: 'query-cache' } })
            if (process.env.NODE_ENV === 'development') {
              console.error('[query error]', error)
            }
          },
        }),
        mutationCache: new MutationCache({ onError: reportMutationError }),
      })
  )

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>{children}</TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}
